interface TourData {
  showGrandTour: boolean;
  signupDate: string;
  hasCompletedTour: boolean;
}

const TOUR_STORAGE_KEY = "affiliate_tour_data";
const TOUR_DURATION_DAYS = 14; // 2 weeks

export const tourManager = {
  /**
   * Get tour data from localStorage
   */
  getTourData(): TourData | null {
    try {
      const stored = localStorage.getItem(TOUR_STORAGE_KEY);
      if (!stored) return null;

      const data: TourData = JSON.parse(stored);
      return data;
    } catch (error) {
      console.error("Error parsing tour data from localStorage:", error);
      return null;
    }
  },

  /**
   * Set tour data in localStorage
   */
  setTourData(data: TourData): void {
    try {
      localStorage.setItem(TOUR_STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      console.error("Error saving tour data to localStorage:", error);
    }
  },

  /**
   * Initialize tour data for a new user
   */
  initializeTourData(): void {
    const tourData: TourData = {
      showGrandTour: true,
      signupDate: new Date().toISOString(),
      hasCompletedTour: false,
    };
    this.setTourData(tourData);
  },

  /**
   * Check if grand tour should be shown based on signup date
   */
  shouldShowGrandTour(): boolean {
    const tourData = this.getTourData();
    if (!tourData) return false;

    // If user has completed the tour, don't show it again
    if (tourData.hasCompletedTour) return false;

    const signupDate = new Date(tourData.signupDate);
    const currentDate = new Date();
    const daysSinceSignup = Math.floor(
      (currentDate.getTime() - signupDate.getTime()) / (1000 * 60 * 60 * 24)
    );

    // Show grand tour for 2 weeks after signup, but don't automatically hide it
    if (daysSinceSignup <= TOUR_DURATION_DAYS) {
      return tourData.showGrandTour;
    }

    // After 2 weeks, still show the tour if it hasn't been manually dismissed
    // Only hide it if the user explicitly completed it
    return tourData.showGrandTour;
  },

  /**
   * Hide the grand tour (user manually dismissed it)
   */
  hideGrandTour(): void {
    const tourData = this.getTourData();
    if (tourData) {
      this.setTourData({
        ...tourData,
        showGrandTour: false,
      });
    }
  },

  /**
   * Complete the grand tour (user finished the tour guide)
   */
  completeGrandTour(): void {
    const tourData = this.getTourData();
    if (tourData) {
      this.setTourData({
        ...tourData,
        showGrandTour: false,
        hasCompletedTour: true,
      });
    }
  },

  /**
   * Dismiss the grand tour (user closed it without completing)
   */
  dismissGrandTour(): void {
    const tourData = this.getTourData();
    if (tourData) {
      this.setTourData({
        ...tourData,
        showGrandTour: false,
        hasCompletedTour: false, // Keep it false so tour can be shown again
      });
    }
  },

  /**
   * Reset tour data (for testing or admin purposes)
   */
  resetTourData(): void {
    localStorage.removeItem(TOUR_STORAGE_KEY);
  },

  /**
   * Get days remaining until tour expires
   */
  getDaysRemaining(): number {
    const tourData = this.getTourData();
    if (!tourData) return 0;

    const signupDate = new Date(tourData.signupDate);
    const currentDate = new Date();
    const daysSinceSignup = Math.floor(
      (currentDate.getTime() - signupDate.getTime()) / (1000 * 60 * 60 * 24)
    );

    return Math.max(0, TOUR_DURATION_DAYS - daysSinceSignup);
  },

  /**
   * Get current tour status for debugging
   */
  getTourStatus(): {
    hasData: boolean;
    showGrandTour: boolean;
    signupDate: string | null;
    daysRemaining: number;
    shouldShow: boolean;
  } {
    const tourData = this.getTourData();
    const shouldShow = this.shouldShowGrandTour();
    const daysRemaining = this.getDaysRemaining();

    return {
      hasData: !!tourData,
      showGrandTour: tourData?.showGrandTour || false,
      signupDate: tourData?.signupDate || null,
      daysRemaining,
      shouldShow,
    };
  },
};
